import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, Clock, Info, Bot, MessageSquareQuote, Bell, X, Mail, Phone, Plus, Share2, Copy, Link, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import Vapi from "@vapi-ai/web";

// Define expected structure
interface SpaWebsiteDetails {
  businessName?: string;
  hours?: { day: string; time: string, hours: string }[];
  location?: { address?: string; mapUrl?: string };
  testimonials?: { text: string; author?: string }[];
  contactInfo?: {
    email?: string;
    phone?: string;
  };
}

// Skeleton Loader
const SkeletonLoader = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-6 w-3/5 bg-brutal-gray rounded-md border border-brutal-black"></div>
    <div className="h-5 w-4/5 bg-brutal-gray rounded-md border border-brutal-black"></div>
    <div className="h-5 w-full bg-brutal-gray rounded-md border border-brutal-black"></div>
  </div>
);

// Agent Avatar Skeleton
const AgentSkeleton = () => (
  <div className="flex flex-col items-center my-8 animate-pulse">
    <div className="h-40 w-40 bg-brutal-gray rounded-full border-4 border-brutal-black mb-4"></div>
    <div className="h-6 w-48 bg-brutal-gray rounded-md border border-brutal-black"></div>
  </div>
);

// Section Skeleton
const SectionSkeleton = ({ title, icon: Icon }: { title: string, icon: React.ElementType }) => (
  <div className="bg-spa-cream border-4 border-brutal-black shadow-lg p-8 rounded-lg mb-6">
    <h3 className="text-xl font-semibold text-brutal-black mb-4 flex items-center font-mono uppercase">
      <Icon className="h-5 w-5 mr-2 text-brutal-dark" /> 
      {title}
    </h3>
    <SkeletonLoader />
  </div>
);

const AgentPage: React.FC = () => {
  const location = useLocation();
  const { websiteUrl } = location.state || {};
  const [spaWebsiteDetails, setSpaWebsiteDetails] = useState<SpaWebsiteDetails>({});
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [companySummary, setCompanySummary] = useState<string>("");
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [agentLoading, setAgentLoading] = useState<boolean>(true);
  const [showNotifyDialog, setShowNotifyDialog] = useState<boolean>(false);
  const [notifyEmail, setNotifyEmail] = useState<string>("");
  const [notifyPhone, setNotifyPhone] = useState<string>("");
  const [notifyFirstName, setNotifyFirstName] = useState<string>("");
  const [notificationSubmitted, setNotificationSubmitted] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>("");
  const [assistantId, setAssistantId] = useState<string>("");
  const [callActive, setCallActive] = useState<boolean>(false);
  const [callEndedMessage, setCallEndedMessage] = useState<string>("");
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const vapiRef = useRef<Vapi | null>(null);

  // Check for assistantId in URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const assistantIdFromUrl = queryParams.get('assistantId');
    
    if (assistantIdFromUrl) {
      setAssistantId(assistantIdFromUrl);
      getSpaByAssistantId(assistantIdFromUrl);
    } else if (websiteUrl) {
      getWebsiteDetails();
    } else {
      // No assistantId or websiteUrl, redirect to home
      navigate("/");
    }
  }, []);

  // New function to get spa details by assistant ID
  const getSpaByAssistantId = async (assistantId: string) => {
    try {
      setLoading(true);
      
      const apiUrl = "https://fos-server-140931751981.us-central1.run.app";
      const apiKey = import.meta.env.VITE_FOS_API_KEY;
      
      const response = await fetch(`${apiUrl}/landingPage/getSpaByAssistantId?assistantId=${assistantId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data[0].data) {
          setSpaWebsiteDetails(data.data[0].data);
          setLoading(false);
          setAgentLoading(false);
        } else {
          console.error("No spa details received");
          navigate("/");
        }
      } else {
        console.error("Error fetching spa by assistant ID:", response.status);
        navigate("/");
      }
    } catch (error) {
      console.error("Error fetching spa by assistant ID:", error);
      navigate("/");
    }
  };

  const getWebsiteDetails = useCallback(async () => {
    try {
      // Make sure we have a URL to process
      if (!websiteUrl) {
        console.error("No website URL provided");
        navigate("/");
        return;
      }

      const n8nWebhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL;
      const response = await fetch(n8nWebhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          websiteUrl,
        }),
      });
      
      // Handle the response regardless of status code
      const data = await response.json();
      
      if (data) {
        setSpaWebsiteDetails(data);
        setLoading(false);
      } else {
        console.error("No data received from webhook");
        navigate("/");
      }
    } catch (error) {
      console.error("Error fetching website details:", error);
      navigate("/");
    }
  }, [websiteUrl, navigate]);

  useEffect(() => {
    if (websiteUrl && !spaWebsiteDetails.businessName) {
      getWebsiteDetails();
    }
  }, [websiteUrl, navigate, getWebsiteDetails, spaWebsiteDetails.businessName]);

  // Move company summary to a separate useEffect that depends on spaWebsiteDetails
  useEffect(() => {
    const getCompanySummary = async () => {
      try {
        if (!spaWebsiteDetails.businessName) return;
        
        setSummaryLoading(true);
        
        const apiKey = import.meta.env.VITE_FOS_API_KEY || "";
        const response = await fetch(`https://fos-server-140931751981.us-central1.run.app/landingPage/createCompanySummary`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": apiKey,
          },
          body: JSON.stringify({
            websiteData: spaWebsiteDetails,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          
          setCompanySummary(data.data);
        } else {
          console.error("Error fetching company summary:", response.status);
        } 
      } catch (error) {
        console.error("Error fetching company summary:", error);
      } finally {
        setSummaryLoading(false);
      }
    };

    if (spaWebsiteDetails.businessName && !companySummary) {
      getCompanySummary();
    }
  }, [spaWebsiteDetails, companySummary]);

  // Update this function to use GET request with query parameters
  const getAssistant = useCallback(async () => {
    try {
      if (!spaWebsiteDetails.businessName) return;
      
      setAgentLoading(true);
      
      const apiUrl = "https://fos-server-140931751981.us-central1.run.app";
      const apiKey = import.meta.env.VITE_FOS_API_KEY;
      const queryParams = new URLSearchParams({
        assistantId: assistantId || '',
        name: `Jess - ${spaWebsiteDetails.businessName}`
      }).toString();
      
      const response = await fetch(`${apiUrl}/vapi/getAssistant?${queryParams}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.assistant.id) {
          setAssistantId(data.assistant.id);
          setAgentLoading(false);
        } else {

          console.error("No assistant ID received");
        }
      } else {
        console.error("Error fetching assistant:", response.status);
      }
    } catch (error) {
      console.error("Error fetching assistant:", error);
    }
  }, [spaWebsiteDetails.businessName, assistantId]);

  // Add a useEffect to call getAssistant when companySummary is available
  useEffect(() => {
    if (spaWebsiteDetails.businessName && companySummary && !assistantId) {
      getAssistant();
    }
  }, [spaWebsiteDetails.businessName, companySummary, assistantId, getAssistant]);

  // Initialize Vapi instance once
  useEffect(() => {
    const vapiApiKey = import.meta.env.VITE_VAPI_API_KEY;
    if (vapiApiKey) {
      vapiRef.current = new Vapi(vapiApiKey);
      
      // Add event listener for call ending
      if (vapiRef.current) {
        vapiRef.current.on('error', (error) => {
          console.log('Vapi error:', error);
          if (error.errorMsg === "Meeting has ended" || (error.error && error.error.type === "no-room")) {
            setCallActive(false);
            setCallEndedMessage("Oops! The call has ended. Please try again.");
            
            // Clear the message after 5 seconds
            setTimeout(() => {
              setCallEndedMessage("");
            }, 5000);
          }
        });
      }
    }
  }, []);

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!spaWebsiteDetails.businessName) {
        console.error("No business name found");
        return;
      }

      if (!notifyEmail && !notifyPhone) {
        setValidationError("Please enter an email or phone number to be notified.");
        return;
      }
      
      setValidationError(""); // Clear any previous errors
      
      const response = await fetch(`https://facultyofskin.app.n8n.cloud/webhook/92f6986f-94e5-45dc-b063-3563fc42b353`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: notifyEmail,
          phone: notifyPhone,
          firstName: notifyFirstName,
          businessName: spaWebsiteDetails.businessName,
        }),
      });

      if (response.ok) {
        setNotificationSubmitted(true);
        // Close dialog after a delay
        setTimeout(() => {
          setShowNotifyDialog(false);
          setNotificationSubmitted(false);
        }, 2000);
      } else {
        console.error("Error sending notification");
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const startCall = () => {
    if (vapiRef.current && assistantId) {
      setCallEndedMessage(""); // Clear any previous message
      
      vapiRef.current.start(assistantId);
      setCallActive(true);
    } else {
      console.log("Vapi or assistantId not available");
    }
  };

  const stopCall = () => {
    if (vapiRef.current) {
      vapiRef.current.stop();
      setCallActive(false);
    }
  };

  const handleShareLink = () => {
    // Create a shareable URL that includes the assistant ID
    const baseUrl = window.location.href.split('?')[0]; // Get the base URL without query params
    const shareUrl = `${baseUrl}?assistantId=${assistantId}`;
    
    // Use the Clipboard API to copy the link
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
      });
  };

  useEffect(() => {
    document.title = spaWebsiteDetails.businessName
      ? `${spaWebsiteDetails.businessName} - Spa Details`
      : "Spa Details";
  }, [spaWebsiteDetails.businessName]);

  return (
    <div className="min-h-screen bg-spa-beige p-8 flex items-center justify-center font-sans">
      <div className="w-full max-w-3xl">
        
        {/* Header Section - Always shown */}
        <div className="bg-white border-4 border-brutal-black shadow-lg p-8 rounded-lg mb-6">
          <div className="text-center py-4">
            <h1 className="text-3xl font-bold text-brutal-black font-mono uppercase">
              {loading ? (
                <span className="block h-10 w-3/4 bg-brutal-gray rounded-md border border-brutal-black mx-auto animate-pulse"></span>
              ) : (
                spaWebsiteDetails.businessName || "Your Spa Assistant"
              )}
            </h1>
          </div>
        </div>

        {/* Company Description Section - Always shown */}
        {summaryLoading ? (
          <SectionSkeleton title="About" icon={Info} />
        ) : (
          <div className="bg-spa-cream border-4 border-brutal-black shadow-lg p-8 rounded-lg mb-6">
            <h3 className="text-xl font-semibold text-brutal-black mb-4 flex items-center font-mono uppercase">
              <Info className="h-5 w-5 mr-2 text-brutal-dark" /> 
              About
            </h3>
            <div className="text-brutal-charcoal text-lg">
              {companySummary ? (
                <ReactMarkdown>
                  {companySummary}
                </ReactMarkdown>
              ) : (
                "No company description available"
              )}
            </div>
          </div>
        )}

        {/* Agent Avatar - Moved up below the about section */}
        <div className="bg-spa-cream border-4 border-brutal-black shadow-lg p-8 rounded-lg mb-6">
          <div className="flex items-center justify-center mb-4">
            <Bot className="h-6 w-6 mr-2 text-brutal-dark flex-shrink-0" />
            <h3 className="text-xl font-semibold text-brutal-black font-mono uppercase m-0">
              {!agentLoading ? "We built a custom assistant" : "We are building a custom assistant"}
            </h3>
          </div>
          
          {/* Business name on a new line */}
          <p className="text-lg font-bold text-brutal-dark text-center mb-6 -mt-2">
            for {spaWebsiteDetails.businessName || "your spa"}!
          </p>
          
          {agentLoading ? (
            <>
              <AgentSkeleton />
              <div className="flex justify-center">
                <button 
                  disabled={loading}
                  className="mt-4 bg-brutal-pink hover:bg-brutal-pink/80 text-brutal-black font-bold py-2 px-6 rounded-md border-2 border-brutal-black shadow-brutal transition-all hover:translate-y-[-2px] hover:shadow-brutal-lg flex items-center"
                  onClick={() => setShowNotifyDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Join the Waitlist
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center">
              {/* Avatar circle - now a button */}
              <div className="relative mb-6">
                <button 
                  onClick={callActive ? stopCall : startCall}
                  className={`h-40 w-40 rounded-full border-4 border-brutal-black flex items-center justify-center
                    ${callActive ? 'bg-brutal-pink' : 'bg-spa-beige'} 
                    hover:bg-spa-cream hover:shadow-brutal-lg transition-all duration-300 cursor-pointer transform hover:scale-105`}
                >
                  <Bot className={`h-20 w-20 ${callActive ? 'text-green-400' : 'text-brutal-dark'} 
                    transition-colors duration-300 hover:text-brutal-black`} />
                </button>
                
                {/* Status indicator */}
                <div className={`absolute -bottom-2 right-0 px-3 py-1 rounded-full border-2 border-brutal-black font-bold text-sm
                  ${callActive 
                    ? 'bg-green-400 text-brutal-black animate-pulse' 
                    : 'bg-brutal-gray text-brutal-dark'}`}>
                  {callActive ? 'ACTIVE' : 'READY'}
                </div>
              </div>
              
              <p className="text-brutal-charcoal text-center mb-2">
                Meet Jess, your personal spa assistant!
              </p>
              {/* Call instruction text */}
              <p className="text-brutal-charcoal text-center mb-4 text-sm">
                {callActive ? "Click the avatar to end call" : "Click the avatar to start call"}
              </p>
              
              {/* Call ended message */}
              {callEndedMessage && (
                <div className="bg-brutal-pink/20 border-2 border-brutal-black rounded-md p-3 mb-4 text-center">
                  <p className="text-brutal-dark font-medium">{callEndedMessage}</p>
                </div>
              )}
              
              {/* Call container */}
              <div id="vapi-container" className="w-full mb-1 min-h-12"></div>
              
              {/* Only show the waitlist button now */}
              <div className="flex flex-col gap-3 items-center">
                <div className="flex flex-row gap-3">
                  <button 
                    disabled={agentLoading}
                    className="bg-brutal-cream hover:bg-brutal-cream/80 text-brutal-black font-bold py-3 px-8 rounded-md border-2 border-brutal-black shadow-brutal transition-all hover:translate-y-[-2px] hover:shadow-brutal-lg flex items-center"
                    onClick={() => setShowNotifyDialog(true)}
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Join the Waitlist
                  </button>
                  
                  <button 
                    className="bg-brutal-blue hover:bg-brutal-blue/80 text-brutal-black font-bold py-3 px-8 rounded-md border-2 border-brutal-black shadow-brutal transition-all hover:translate-y-[-2px] hover:shadow-brutal-lg flex items-center"
                    onClick={() => setShowShareDialog(true)}
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Business Hours Section - Only shown if hours exist and have valid data */}
        {loading ? (
          <SectionSkeleton title="Business Hours" icon={Clock} />
        ) : (
          spaWebsiteDetails.hours && 
          spaWebsiteDetails.hours.length > 0 && (
            <div className="bg-clay-cream border-4 border-brutal-black shadow-lg p-8 rounded-lg mb-6">
              <h3 className="text-xl font-semibold text-clay-forest mb-4 flex items-center font-mono uppercase">
                <Clock className="h-5 w-5 mr-2 text-brutal-dark" /> Business Hours
              </h3>
              <ul className="space-y-2">
                {spaWebsiteDetails.hours.map((hour, index) => (
                  <li key={index} className="text-brutal-charcoal text-lg">
                    <strong className="text-clay-forest">{hour.day}:</strong> {hour.time ? hour.time : hour.hours}
                  </li>
                ))}
              </ul>
            </div>
          )
        )}

        {/* Location Section - Only shown if address exists */}
        {loading ? (
          <SectionSkeleton title="Location" icon={MapPin} />
        ) : (
          spaWebsiteDetails.location && 
          spaWebsiteDetails.location.address && 
          spaWebsiteDetails.location.address !== "No information found" && (
            <div className="bg-brutal-white border-4 border-brutal-black shadow-lg p-8 rounded-lg mb-6">
              <h3 className="text-xl font-semibold text-brutal-black mb-4 flex items-center font-mono uppercase">
                <MapPin className="h-5 w-5 mr-2 text-brutal-dark" /> Location
              </h3>
              <div>
                <p className="text-brutal-charcoal text-lg">{spaWebsiteDetails.location.address}</p>
                {spaWebsiteDetails.location.mapUrl && 
                 spaWebsiteDetails.location.mapUrl !== "No information found" && (
                  <a 
                    href={spaWebsiteDetails.location.mapUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-brutal-dark font-bold hover:underline bg-brutal-pink px-4 py-2 inline-block mt-3 border-2 border-brutal-black"
                  >
                    View on Map
                  </a>
                )}
              </div>
            </div>
          )
        )}

        {/* Testimonials Section - Only shown if testimonials exist */}
        {!loading && spaWebsiteDetails.testimonials && 
         spaWebsiteDetails.testimonials.length > 0 && 
         spaWebsiteDetails.testimonials[0].text !== "No information found" && (
          <div className="bg-brutal-pink border-4 border-brutal-black shadow-lg p-8 rounded-lg mb-6">
            <h3 className="text-xl font-semibold text-brutal-black mb-4 flex items-center font-mono uppercase">
              <MessageSquareQuote className="h-5 w-5 mr-2 text-brutal-dark" /> Testimonials
            </h3>
            <div className="space-y-4">
              {spaWebsiteDetails.testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white border-2 border-brutal-black p-4 rounded-lg">
                  <p className="text-brutal-charcoal italic">"{testimonial.text}"</p>
                  {testimonial.author && (
                    <p className="text-brutal-dark font-semibold text-right mt-2">- {testimonial.author}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Notification Dialog */}
      {showNotifyDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowNotifyDialog(false)}
        >
          <div 
            className="bg-white opacity-90 border-4 border-brutal-black shadow-brutal-lg rounded-lg p-6 w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowNotifyDialog(false)}
              className="absolute top-2 right-2 text-brutal-black hover:text-brutal-dark"
            >
              <X className="h-6 w-6" />
            </button>
            
            <h3 className="text-xl font-bold text-brutal-black mb-4 font-mono uppercase text-center">
              Join the Waitlist
            </h3>
            
            {notificationSubmitted ? (
              <div className="text-center py-8">
                <p className="text-brutal-dark text-lg font-bold">Thank you!</p>
                <p>We'll notify you when we launch!</p>
              </div>
            ) : (
              <form onSubmit={handleNotifySubmit} className="space-y-4">
                <div>
                  <label className="text-brutal-dark font-bold mb-2 flex items-center">
                    <span className="mr-2">�</span> First Name
                  </label>
                  <input
                    type="text"
                    value={notifyFirstName}
                    onChange={(e) => setNotifyFirstName(e.target.value)}
                    className="w-full p-2 border-2 border-brutal-black rounded-md focus:outline-none focus:ring-2 focus:ring-brutal-pink"
                    placeholder="Your first name"
                  />
                </div>
                
                <div>
                  <label className="text-brutal-dark font-bold mb-2 flex items-center">
                    <Mail className="h-4 w-4 mr-2" /> Email
                  </label>
                  <input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className="w-full p-2 border-2 border-brutal-black rounded-md focus:outline-none focus:ring-2 focus:ring-brutal-pink"
                    placeholder={`name@${spaWebsiteDetails.businessName}.com`}
                  />
                </div>
                
                <div>
                  <label className="text-brutal-dark font-bold mb-2 flex items-center">
                    <Phone className="h-4 w-4 mr-2" /> Phone Number (optional)
                  </label>
                  <input
                    type="tel"
                    value={notifyPhone}
                    onChange={(e) => setNotifyPhone(e.target.value)}
                    className="w-full p-2 border-2 border-brutal-black rounded-md focus:outline-none focus:ring-2 focus:ring-brutal-pink"
                    placeholder="(123) 456-7890"
                  />
                </div>
                
                {validationError && (
                  <div className="text-red-600 font-bold text-sm p-2 bg-red-100 border border-red-300 rounded">
                    {validationError}
                  </div>
                )}
                
                <div className="flex justify-center pt-2">
                  <button
                    type="submit"
                    className="bg-brutal-pink hover:bg-brutal-pink/80 text-brutal-black font-bold py-2 px-6 rounded-md border-2 border-brutal-black shadow-brutal transition-all hover:translate-y-[-2px] hover:shadow-brutal-lg flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Join the Waitlist
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Share Dialog */}
      {showShareDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowShareDialog(false)}
        >
          <div 
            className="bg-white opacity-90 border-4 border-brutal-black shadow-brutal-lg rounded-lg p-6 w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowShareDialog(false)}
              className="absolute top-2 right-2 text-brutal-black hover:text-brutal-dark"
            >
              <X className="h-6 w-6" />
            </button>
            
            <h3 className="text-xl font-bold text-brutal-black mb-6 font-mono uppercase text-center">
              Share This Assistant
            </h3>
            
            <div className="space-y-6">
              {/* Copy Link Button */}
              <div className="flex flex-col items-center">
                <button
                  onClick={handleShareLink}
                  className={`w-full bg-brutal-blue hover:bg-brutal-blue/80 text-brutal-black font-bold py-3 px-6 rounded-md border-2 border-brutal-black shadow-brutal transition-all hover:translate-y-[-2px] hover:shadow-brutal-lg flex items-center justify-center ${linkCopied ? 'bg-green-400' : ''}`}
                  disabled={!assistantId}
                >
                  {linkCopied ? (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      Link Copied!
                    </>
                  ) : (
                    <>
                      <Link className="h-5 w-5 mr-2" />
                      Copy Link
                    </>
                  )}
                </button>
                {!assistantId && (
                  <p className="text-red-500 text-sm mt-2">
                    Assistant is still loading. Please try again in a moment.
                  </p>
                )}
              </div>
              
              <p className="text-center text-brutal-dark text-sm mt-4">
                Share this assistant with your co-workers or employees who might be interested!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentPage;
